import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, Scale, Car, Home, FileText, CheckCircle, AlertTriangle, 
  Clock, Send, Mail, RefreshCw, Filter, Search, Eye, Printer,
  TrendingUp, Users, DollarSign, Calendar, FileSignature
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import ESignatureWorkflow from '../components/ESignatureWorkflow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNovationDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [autoNovations, setAutoNovations] = useState([]);
  const [mortgageNovations, setMortgageNovations] = useState([]);
  const [showESignature, setShowESignature] = useState(false);
  const [selectedNovation, setSelectedNovation] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending_consent: 0,
    pending_signatures: 0,
    fully_executed: 0,
    total_value: 0
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    // Wait for user data to load before checking admin status
    if (user === null) {
      return; // Still loading
    }
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAllNovations();
  }, [token, user, navigate]);

  const fetchAllNovations = async () => {
    setLoading(true);
    try {
      const [autoRes, mortgageRes] = await Promise.all([
        fetch(`${API}/admin/auto-financing/novations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/mortgage-refinance/novations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      let autos = [];
      let mortgages = [];

      if (autoRes.ok) {
        const autoData = await autoRes.json();
        autos = (autoData.agreements || []).map(a => ({ ...a, type: 'auto' }));
      }

      if (mortgageRes.ok) {
        const mortgageData = await mortgageRes.json();
        mortgages = (mortgageData.agreements || []).map(a => ({ ...a, type: 'mortgage' }));
      }

      setAutoNovations(autos);
      setMortgageNovations(mortgages);

      // Calculate stats
      const all = [...autos, ...mortgages];
      const newStats = {
        total: all.length,
        pending_consent: all.filter(n => n.status === 'pending_lender_consent' || n.status === 'pending_signatures').length,
        pending_signatures: all.filter(n => n.status === 'pending_signatures').length,
        fully_executed: all.filter(n => n.status === 'fully_executed').length,
        total_value: all.reduce((sum, n) => sum + (n.settlement_amount || n.payoff_amount || 0), 0)
      };
      setStats(newStats);

    } catch (error) {
      console.error('Error fetching novations:', error);
      toast.error('Failed to load novation data');
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (novation) => {
    try {
      const endpoint = novation.type === 'auto' 
        ? `${API}/admin/auto-financing/novation/${novation.agreement_id}/send-reminder`
        : `${API}/admin/mortgage-refinance/novation/${novation.agreement_id}/send-reminder`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Reminder sent successfully');
        fetchAllNovations(); // Refresh the list
      } else {
        const error = await response.json();
        toast.error(error.detail || error.error || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  const getAllNovations = () => {
    let all = [];
    if (activeTab === 'all' || activeTab === 'auto') {
      all = [...all, ...autoNovations];
    }
    if (activeTab === 'all' || activeTab === 'mortgage') {
      all = [...all, ...mortgageNovations];
    }
    
    // Apply filters
    if (statusFilter !== 'all') {
      all = all.filter(n => n.status === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      all = all.filter(n => 
        (n.agreement_id || '').toLowerCase().includes(term) ||
        (n.borrower_name || '').toLowerCase().includes(term) ||
        (n.original_lender_name || n.original_creditor_name || '').toLowerCase().includes(term) ||
        (n.property_address || n.vehicle_description || '').toLowerCase().includes(term)
      );
    }
    
    // Sort by created_at descending
    return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending_lender_consent': 'bg-orange-500/20 text-orange-400',
      'pending_signatures': 'bg-yellow-500/20 text-yellow-400',
      'fully_executed': 'bg-green-500/20 text-green-400',
      'cancelled': 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {(status || 'unknown').replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Show loading while checking auth or loading data
  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#b8860b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading novation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="admin-novation-dashboard">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                ADMIN
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#b8860b]" />
                Novation Agreements Management
              </h1>
              <p className="text-xs text-gray-500">Manage Auto Loan and Mortgage Novation Agreements</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchAllNovations}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Agreements</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending Consent</p>
                  <p className="text-2xl font-bold text-orange-400">{stats.pending_consent}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending Signatures</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.pending_signatures}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Fully Executed</p>
                  <p className="text-2xl font-bold text-green-400">{stats.fully_executed}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#b8860b]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#b8860b]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Value</p>
                  <p className="text-xl font-bold text-[#b8860b]">{formatCurrency(stats.total_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a] mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Type Tabs */}
              <div className="flex gap-2">
                <Button
                  variant={activeTab === 'all' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('all')}
                  className={activeTab === 'all' ? 'bg-[#b8860b]' : 'border-[#1a2d4a] text-gray-400'}
                  size="sm"
                >
                  All ({autoNovations.length + mortgageNovations.length})
                </Button>
                <Button
                  variant={activeTab === 'auto' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('auto')}
                  className={activeTab === 'auto' ? 'bg-purple-600' : 'border-[#1a2d4a] text-gray-400'}
                  size="sm"
                >
                  <Car className="w-4 h-4 mr-1" />
                  Auto ({autoNovations.length})
                </Button>
                <Button
                  variant={activeTab === 'mortgage' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('mortgage')}
                  className={activeTab === 'mortgage' ? 'bg-indigo-600' : 'border-[#1a2d4a] text-gray-400'}
                  size="sm"
                >
                  <Home className="w-4 h-4 mr-1" />
                  Mortgage ({mortgageNovations.length})
                </Button>
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-[#0a1628] border border-[#1a2d4a] text-white rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Statuses</option>
                <option value="pending_lender_consent">Pending Consent</option>
                <option value="pending_signatures">Pending Signatures</option>
                <option value="fully_executed">Fully Executed</option>
              </select>

              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by ID, borrower, lender, or property..."
                    className="pl-10 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Novations List */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white">Novation Agreements</CardTitle>
            <CardDescription>
              {getAllNovations().length} agreement(s) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {getAllNovations().length > 0 ? (
              <div className="space-y-4">
                {getAllNovations().map((novation) => (
                  <div 
                    key={novation.agreement_id} 
                    className={`p-4 rounded-lg border ${
                      novation.type === 'auto' 
                        ? 'bg-purple-900/10 border-purple-700/30' 
                        : 'bg-indigo-900/10 border-indigo-700/30'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          novation.type === 'auto' ? 'bg-purple-500/20' : 'bg-indigo-500/20'
                        }`}>
                          {novation.type === 'auto' 
                            ? <Car className="w-5 h-5 text-purple-400" />
                            : <Home className="w-5 h-5 text-indigo-400" />
                          }
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {novation.type === 'auto' ? 'Auto Loan' : 'Mortgage'} Novation
                          </p>
                          <p className="text-xs text-gray-500 font-mono">{novation.agreement_id}</p>
                        </div>
                      </div>
                      {getStatusBadge(novation.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-gray-500 text-xs">Borrower</p>
                        <p className="text-white">{novation.borrower_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">
                          {novation.type === 'auto' ? 'Creditor' : 'Lender'}
                        </p>
                        <p className="text-white">
                          {novation.original_lender_name || novation.original_creditor_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">
                          {novation.type === 'auto' ? 'Vehicle' : 'Property'}
                        </p>
                        <p className="text-white text-xs truncate">
                          {novation.vehicle_description || novation.property_address}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Settlement Amount</p>
                        <p className="text-[#b8860b] font-bold">
                          {formatCurrency(novation.settlement_amount || novation.payoff_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Created</p>
                        <p className="text-white">{formatDate(novation.created_at)}</p>
                      </div>
                    </div>

                    {/* Consent Status */}
                    {novation.consent_email_sent_to && (
                      <div className="text-xs text-blue-400 mb-2 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        Consent request sent to: {novation.consent_email_sent_to}
                        {novation.consent_email_sent_at && (
                          <span className="text-gray-500 ml-2">
                            ({formatDate(novation.consent_email_sent_at)})
                          </span>
                        )}
                      </div>
                    )}

                    {novation.lender_consent_received && (
                      <div className="text-xs text-green-400 mb-2 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Consent received from: {novation.lender_consent_by}
                        {novation.lender_consent_date && (
                          <span className="text-gray-500 ml-2">
                            ({formatDate(novation.lender_consent_date)})
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-3 pt-3 border-t border-[#1a2d4a]">
                      <Button size="sm" variant="outline" className="border-[#1a2d4a] text-gray-400">
                        <Eye className="w-4 h-4 mr-1" /> View Details
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#1a2d4a] text-gray-400">
                        <Printer className="w-4 h-4 mr-1" /> Print
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          setSelectedNovation(novation);
                          setShowESignature(true);
                        }}
                        data-testid={`esign-${novation.agreement_id}`}
                      >
                        <FileSignature className="w-4 h-4 mr-1" /> E-Sign
                      </Button>
                      {(novation.status === 'pending_lender_consent' || novation.status === 'pending_signatures') && 
                       !novation.lender_consent_received && (
                        <Button 
                          size="sm" 
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => sendReminder(novation)}
                          data-testid={`send-reminder-${novation.agreement_id}`}
                        >
                          <Send className="w-4 h-4 mr-1" /> 
                          {novation.reminder_count > 0 ? `Send Reminder #${novation.reminder_count + 1}` : 'Send Reminder'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Scale className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No novation agreements found</p>
                <p className="text-sm mt-2">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Agreements will appear here when users create them'}
                </p>
              </div>
            )}

      {/* E-Signature Modal */}
      <ESignatureWorkflow 
        isOpen={showESignature} 
        onClose={() => {
          setShowESignature(false);
          setSelectedNovation(null);
        }}
        documentData={selectedNovation ? {
          agreement_id: selectedNovation.agreement_id,
          document_name: `${selectedNovation.type === 'auto' ? 'Auto' : 'Mortgage'} Novation Agreement`,
          borrower_name: selectedNovation.borrower_name
        } : null}
        agreementType={selectedNovation?.type === 'auto' ? 'auto_novation' : 'mortgage_novation'}
      />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminNovationDashboard;
